<template>
  <div class="model-comparison-detail">
    <a-page-header
      title="对比计划详情"
      :sub-title="plan.name"
      @back="goBack"
    >
      <template #extra>
        <a-space>
          <a-tag :color="getStatusColor(plan.status)" style="font-size: 14px">
            {{ getStatusText(plan.status) }}
          </a-tag>
          <a-button
            v-if="plan.status === 'running'"
            danger
            :loading="cancelling"
            @click="handleCancel"
          >
            停止执行
          </a-button>
          <a-button
            v-else
            type="primary"
            :loading="executing"
            @click="handleExecute"
          >
            开始执行
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <!-- 区块一：计划配置 -->
      <a-card title="计划配置" style="margin-top: 16px">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="计划名称">{{ plan.name }}</a-descriptions-item>
          <a-descriptions-item label="每模型执行次数">{{ plan.runs_per_model }}</a-descriptions-item>
          <a-descriptions-item label="描述" :span="2">{{ plan.description || '-' }}</a-descriptions-item>
          <a-descriptions-item label="裁判模型">
            <a-select
              v-model:value="judgeModelId"
              style="width: 260px"
              placeholder="选择裁判模型"
              @change="handleJudgeModelChange"
              :loading="modelsLoading"
            >
              <a-select-option
                v-for="m in availableModels"
                :key="m.id"
                :value="m.id"
              >
                {{ m.model_name }}
              </a-select-option>
            </a-select>
          </a-descriptions-item>
          <a-descriptions-item label="被测模型" :span="2">
            <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
              <a-tag
                v-for="model in plan.models"
                :key="model.id"
                closable
                @close="handleRemoveModel(model)"
                style="margin: 0;"
              >
                {{ model.name }}
              </a-tag>
              <a-button size="small" type="dashed" @click="openAddModelModal">
                + 批量添加模型
              </a-button>
            </div>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 区块二：测试用例管理 -->
      <a-card title="测试用例" style="margin-top: 16px">
        <a-table
          :columns="caseColumns"
          :data-source="plan.cases"
          row-key="id"
          :pagination="false"
          size="middle"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'case_name'">
              {{ record.case_name }}
            </template>
            <template v-else-if="column.key === 'expected_description'">
              <div v-if="editingCaseId !== record.id" @click="startEditCase(record)" style="cursor: pointer; min-height: 22px;">
                <span v-if="record.expected_description">{{ record.expected_description }}</span>
                <span v-else style="color: #bbb">点击编辑期望描述</span>
              </div>
              <a-input
                v-else
                :ref="el => { if (el) caseInputRef = el; }"
                v-model:value="editingCaseValue"
                @blur="saveEditCase(record)"
                @pressEnter="saveEditCase(record)"
                @keyup.esc="cancelEditCase"
              />
            </template>
            <template v-else-if="column.key === 'action'">
              <a-popconfirm
                title="确定移除该用例吗？"
                @confirm="handleRemoveCase(record)"
              >
                <a-button type="link" size="small" danger>移除</a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
        <div style="margin-top: 12px; display: flex; gap: 8px;">
          <a-button type="dashed" style="flex: 1;" @click="openAddCaseModal">
            + 添加用例
          </a-button>
          <a-button @click="openCsvImportModal">
            <template #icon><UploadOutlined /></template>
            导入 CSV
          </a-button>
        </div>
      </a-card>

      <!-- 区块三：对比结果 -->
      <a-card
        v-if="plan.status === 'completed' || (plan.execution_count > 0 && runLogs.length > 0)"
        style="margin-top: 16px"
      >
        <template #title>
          <a-space>
            <span>对比结果</span>
            <template v-if="plan.execution_count > 1">
              <a-select
                :value="selectedExecutionNo"
                style="width: 140px"
                size="small"
                @change="handleExecutionNoChange"
              >
                <a-select-option
                  v-for="n in plan.execution_count"
                  :key="n"
                  :value="n"
                >
                  第 {{ n }} 次执行
                </a-select-option>
              </a-select>
            </template>
            <span v-else-if="plan.execution_count === 1" style="color: #888; font-size: 13px">第 1 次执行</span>
            <a-button
              v-if="matrixData.rows.length > 0"
              size="small"
              @click="handleExportExcel"
            >
              <template #icon><DownloadOutlined /></template>
              导出 Excel
            </a-button>
          </a-space>
        </template>
        <a-spin :spinning="resultsLoading">
          <!-- 统计摘要卡片 -->
          <div v-if="matrixData.rows.length > 0" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;">
            <!-- 最高通过率 -->
            <div style="background: #f6ffed; border: 1px solid #b7eb8f; border-radius: 8px; padding: 16px;">
              <div style="font-size: 12px; color: #52c41a; font-weight: 600; margin-bottom: 4px;">最高通过率模型</div>
              <div style="font-size: 15px; font-weight: 700; color: #262626; margin-bottom: 2px;">{{ summaryStats.bestModel?.model_name ?? '-' }}</div>
              <div style="font-size: 20px; font-weight: 700; color: #52c41a;">{{ summaryStats.bestModel ? formatPercent(summaryStats.bestModel.pass_rate) : '-' }}</div>
            </div>
            <!-- 最低Token消耗 -->
            <div style="background: #e6f7ff; border: 1px solid #91d5ff; border-radius: 8px; padding: 16px;">
              <div style="font-size: 12px; color: #1890ff; font-weight: 600; margin-bottom: 4px;">最低 Token 消耗</div>
              <div style="font-size: 15px; font-weight: 700; color: #262626; margin-bottom: 2px;">{{ summaryStats.cheapestModel?.model_name ?? '-' }}</div>
              <div style="font-size: 20px; font-weight: 700; color: #1890ff;">{{ summaryStats.cheapestModel ? summaryStats.cheapestModel.avg_tokens.toFixed(0) + ' tokens' : '-' }}</div>
            </div>
            <!-- 最快响应 -->
            <div style="background: #fff7e6; border: 1px solid #ffd591; border-radius: 8px; padding: 16px;">
              <div style="font-size: 12px; color: #fa8c16; font-weight: 600; margin-bottom: 4px;">最快平均响应</div>
              <div style="font-size: 15px; font-weight: 700; color: #262626; margin-bottom: 2px;">{{ summaryStats.fastestModel?.model_name ?? '-' }}</div>
              <div style="font-size: 20px; font-weight: 700; color: #fa8c16;">{{ summaryStats.fastestModel ? summaryStats.fastestModel.avg_duration_ms + ' ms' : '-' }}</div>
            </div>
            <!-- 最难用例 -->
            <div style="background: #fff1f0; border: 1px solid #ffa39e; border-radius: 8px; padding: 16px;">
              <div style="font-size: 12px; color: #f5222d; font-weight: 600; margin-bottom: 4px;">最难通过用例</div>
              <div style="font-size: 15px; font-weight: 700; color: #262626; margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ summaryStats.hardestCase?.case_name ?? '-' }}</div>
              <div style="font-size: 20px; font-weight: 700; color: #f5222d;">{{ summaryStats.hardestCase ? formatPercent(summaryStats.hardestCase.pass_rate) : '-' }}</div>
            </div>
          </div>
          <!-- AI 总结 -->
          <div v-if="matrixData.rows.length > 0" style="margin-bottom: 16px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <span style="font-weight: 600; color: #262626;">AI 总结</span>
              <a-button
                size="small"
                :loading="aiSummary.loading"
                :disabled="aiSummary.loading"
                @click="handleGenerateSummary"
              >
                <template #icon><RobotOutlined /></template>
                {{ aiSummary.text ? '重新生成' : '生成总结' }}
              </a-button>
            </div>
            <a-alert
              v-if="aiSummary.text"
              type="info"
              :message="aiSummary.text"
              style="white-space: pre-wrap; line-height: 1.8;"
              :show-icon="false"
            />
          </div>
          <a-table
            v-if="matrixData.columns.length > 0"
            :columns="matrixData.columns"
            :data-source="matrixData.rows"
            row-key="model_id"
            :pagination="false"
            :scroll="{ x: 'max-content' }"
            size="middle"
            bordered
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'model_name'">
                <div style="line-height: 1.4">
                  <div style="font-weight: 600">{{ record.model_name }}</div>
                  <div v-if="record.model_avg_rate != null" :style="{ fontSize: '12px', color: getPassRateColor(record.model_avg_rate) }">
                    均 {{ formatPercent(record.model_avg_rate) }}
                  </div>
                </div>
              </template>
              <template v-else-if="column.isCaseColumn">
                <div
                  v-if="record[column.key]"
                  :style="{ color: getPassRateColor(record[column.key].pass_rate), cursor: 'pointer' }"
                  @click="openRunDrawer(record[column.key])"
                >
                  <div style="font-size: 15px; font-weight: 600;">
                    {{ record[column.key].pass_count }}/{{ record[column.key].total }} 次通过
                  </div>
                  <div style="font-size: 12px;">
                    {{ formatPercent(record[column.key].pass_rate) }}
                  </div>
                </div>
                <span v-else style="color: #ccc">-</span>
              </template>
            </template>
          </a-table>
          <a-empty v-else description="暂无结果数据" />
        </a-spin>
      </a-card>

      <!-- 区块四：执行日志面板 -->
      <a-card
        v-if="plan.status === 'running' || runLogs.length > 0"
        style="margin-top: 16px"
      >
        <template #title>
          <a-space>
            <span>执行日志</span>
            <template v-if="plan.status === 'running'">
              <a-progress
                :percent="progressInfo.total_runs > 0
                  ? Math.round(progressInfo.completed_runs / progressInfo.total_runs * 100)
                  : 0"
                :status="'active'"
                style="width: 200px; margin-bottom: 0"
              />
              <span style="color: #888; font-size: 13px">
                已完成 {{ progressInfo.completed_runs }} / {{ progressInfo.total_runs }} 次执行
              </span>
            </template>
            <a-tag v-else color="success">共 {{ runLogsTotal }} 条</a-tag>
            <span v-if="selectedExecutionNo" style="color: #888; font-size: 12px">第 {{ selectedExecutionNo }} 次执行</span>
          </a-space>
        </template>

        <div v-if="plan.status === 'running' && runLogs.length === 0 && !logsLoading" style="text-align: center; padding: 32px 0; color: #888">
          <a-spin /> 正在等待第一条执行结果...
        </div>
        <a-spin :spinning="logsLoading">
        <div v-for="log in runLogs" :key="log.id" style="border: 1px solid #f0f0f0; border-radius: 6px; margin-bottom: 12px; overflow: hidden">
          <!-- 日志头部 -->
          <div style="display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: #fafafa; flex-wrap: wrap">
            <a-tag :color="log.judge_passed ? 'success' : 'error'" style="margin: 0">
              {{ log.judge_passed ? '✓ 通过' : '✗ 失败' }}
            </a-tag>
            <span style="font-weight: 600">{{ log.case_name }}</span>
            <a-divider type="vertical" />
            <span style="color: #666">{{ log.model_name }}</span>
            <a-divider type="vertical" />
            <span style="color: #888; font-size: 12px">第 {{ log.run_index }} 轮</span>
            <a-divider type="vertical" />
            <span style="color: #888; font-size: 12px">耗时 {{ log.duration_ms }} ms</span>
            <a-divider type="vertical" />
            <span style="color: #888; font-size: 12px">
              Token {{ (log.input_tokens || 0) + (log.output_tokens || 0) }}
            </span>
            <template v-if="log.knowledge_count > 0">
              <a-divider type="vertical" />
              <span style="color: #888; font-size: 12px">引用知识 {{ log.knowledge_count }} 条</span>
            </template>
            <template v-if="log.output_format_valid !== null">
              <a-divider type="vertical" />
              <a-tag :color="log.output_format_valid ? 'processing' : 'warning'" style="margin: 0; font-size: 11px">
                {{ log.output_format_valid ? 'JSON合规' : 'JSON不合规' }}
              </a-tag>
            </template>
          </div>

          <div style="padding: 12px 16px">
            <!-- 提示词 -->
            <div style="margin-bottom: 10px">
              <a style="font-size: 12px; color: #1677ff" @click="toggleLogSection(log, 'input')">
                {{ expandedPrompts[`${log.id}_input`] ? '▼ 收起提示词' : '▶ 展开提示词' }}
              </a>
              <div v-if="expandedPrompts[`${log.id}_input`]" style="margin-top: 8px; background: #f6f8fa; border-radius: 4px; padding: 10px; font-size: 12px; white-space: pre-wrap; max-height: 500px; overflow-y: auto; font-family: monospace">{{ log.input_prompt || '（暂无）' }}</div>
            </div>

            <!-- AI 响应 -->
            <div style="margin-bottom: 10px">
              <a style="font-size: 12px; color: #1677ff" @click="toggleLogSection(log, 'output')">
                {{ expandedPrompts[`${log.id}_output`] ? '▼ 收起模型响应' : '▶ 展开模型响应' }}
              </a>
              <div v-if="expandedPrompts[`${log.id}_output`]" style="margin-top: 8px; background: #f6f8fa; border-radius: 4px; padding: 10px; font-size: 12px; white-space: pre-wrap; max-height: 500px; overflow-y: auto; font-family: monospace">{{ log.raw_output || '（无响应）' }}</div>
            </div>

            <!-- 裁判结论 -->
            <div>
              <a style="font-size: 12px; color: #1677ff" @click="toggleLogSection(log, 'judge')">
                {{ expandedPrompts[`${log.id}_judge`] ? '▼ 收起裁判详情' : '▶ 展开裁判详情' }}
              </a>
              <div v-if="expandedPrompts[`${log.id}_judge`]" style="margin-top: 8px">
                <div style="font-size: 12px; color: #666; margin-bottom: 4px">裁判提示词</div>
                <div style="background: #f6f8fa; border-radius: 4px; padding: 8px; font-size: 12px; white-space: pre-wrap; max-height: 200px; overflow-y: auto; font-family: monospace; margin-bottom: 8px">{{ log.judge_prompt || '（暂无）' }}</div>
                <div style="font-size: 12px; color: #666; margin-bottom: 4px">裁判理由</div>
                <div :style="{ background: log.judge_passed ? '#f6ffed' : '#fff2f0', border: `1px solid ${log.judge_passed ? '#b7eb8f' : '#ffccc7'}`, borderRadius: '4px', padding: '8px', fontSize: '13px' }">
                  {{ log.judge_reason || '-' }}
                </div>
              </div>
              <div v-else style="margin-top: 6px; font-size: 13px; color: #555">展开后加载裁判详情</div>
            </div>
          </div>
        </div>
        <a-pagination
          v-if="runLogsTotal > logPagination.pageSize"
          v-model:current="logPagination.current"
          :page-size="logPagination.pageSize"
          :total="runLogsTotal"
          :show-size-changer="false"
          style="margin-top: 16px; text-align: right"
          @change="fetchRunLogs"
        />
        </a-spin>
      </a-card>
    </a-spin>

    <!-- 添加模型弹窗 -->
    <a-modal
      v-model:open="addModelModalVisible"
      title="添加被测模型"
      @ok="handleAddModel"
      :confirm-loading="addingModel"
      @cancel="addModelModalVisible = false"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="选择模型" required>
          <a-select
            v-model:value="selectedModelIds"
            mode="multiple"
            style="width: 100%"
            placeholder="可多选，支持搜索"
            :loading="modelsLoading"
            show-search
            :filter-option="filterModelOption"
            :max-tag-count="5"
          >
            <a-select-option
              v-for="m in availableModels"
              :key="m.id"
              :value="m.id"
            >
              {{ m.model_name }}（{{ m.model_code }}）
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 添加用例弹窗 -->
    <a-modal
      v-model:open="addCaseModalVisible"
      title="添加测试用例"
      @ok="handleAddCase"
      :confirm-loading="addingCase"
      @cancel="resetAddCaseForm"
      width="600px"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="选择用例" required>
          <a-select
            v-model:value="addCaseForm.test_case_id"
            style="width: 100%"
            placeholder="输入关键词搜索用例"
            show-search
            :filter-option="false"
            :loading="caseSearchLoading"
            @search="handleCaseSearch"
            @change="handleCaseSelectChange"
          >
            <a-select-option
              v-for="c in caseSearchResults"
              :key="c.id"
              :value="c.id"
            >
              {{ c.case_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="期望描述" required>
          <a-textarea
            v-model:value="addCaseForm.expected_description"
            :rows="4"
            placeholder="请输入该用例的期望描述（必填）"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- CSV 导入弹窗 -->
    <a-modal
      v-model:open="showCsvImportModal"
      title="导入 CSV 追加用例"
      :footer="null"
      @cancel="closeCsvImportModal"
      :width="700"
    >
      <!-- 状态一：未解析 -->
      <div v-if="!csvPreviewRows.length && !importResult">
        <p style="color: #888; font-size: 13px; margin-bottom: 16px;">
          CSV 格式要求：包含三列 task_id（任务ID）、步骤顺序（step_order）、判断标准（expected_description），第一行为列名。
        </p>
        <input
          type="file"
          ref="csvFileInputRef"
          accept=".csv"
          style="display:none"
          @change="handleCsvFile"
        />
        <a-button @click="csvFileInputRef.click()">
          <template #icon><UploadOutlined /></template>
          选择 CSV 文件
        </a-button>
      </div>

      <!-- 状态二：已解析预览 -->
      <div v-else-if="csvPreviewRows.length > 0 && !importResult">
        <a-table
          :data-source="csvPreviewRows"
          :row-key="(_, index) => index"
          :pagination="false"
          :scroll="{ y: 300 }"
          :columns="[
            { title: '任务ID', dataIndex: 'task_id', key: 'task_id', width: 160 },
            { title: '步骤顺序', dataIndex: 'step_order', key: 'step_order', width: 100 },
            { title: '判断标准', dataIndex: 'expected_description', key: 'expected_description', ellipsis: true }
          ]"
        />
        <div style="margin-top: 8px; color: #888; font-size: 13px;">
          共 {{ csvPreviewRows.length }} 行
        </div>
        <div style="margin-top: 12px; text-align: right; display: flex; justify-content: flex-end; gap: 8px;">
          <a-button @click="resetCsvPreview">
            重新选择
          </a-button>
          <a-button type="primary" :loading="csvImporting" :disabled="csvImporting" @click="handleImportCsv">
            确认导入
          </a-button>
        </div>
      </div>

      <!-- 状态三：导入完成 -->
      <div v-else-if="importResult">
        <a-result
          :status="importResult.failed === importResult.total ? 'error' : importResult.failed > 0 ? 'warning' : 'success'"
          title="导入完成"
          :sub-title="`共 ${importResult.total} 行：新建并关联 ${importResult.added} 条，复用已有用例 ${importResult.linked} 条，已在计划中跳过 ${importResult.skipped} 条，失败 ${importResult.failed} 条`"
        >
          <template #extra>
            <div v-if="importResult.errors && importResult.errors.length > 0" style="text-align: left; margin-bottom: 16px;">
              <a-collapse>
                <a-collapse-panel key="errors" header="失败详情">
                  <a-list
                    :data-source="importResult.errors"
                    size="small"
                  >
                    <template #renderItem="{ item: e }">
                      <a-list-item>
                        第 {{ e.row }} 行（{{ e.task_id }} / 步骤 {{ e.step_order }}）：{{ e.error }}
                      </a-list-item>
                    </template>
                  </a-list>
                </a-collapse-panel>
              </a-collapse>
            </div>
            <a-button type="primary" @click="closeCsvImportModal">关闭</a-button>
          </template>
        </a-result>
      </div>
    </a-modal>

    <!-- 执行明细抽屉 -->
    <a-drawer
      v-model:open="drawerVisible"
      title="执行明细"
      width="720"
      :destroy-on-close="true"
    >
      <a-spin :spinning="drawerLoading">
      <div v-if="drawerData">
        <!-- 汇总统计 -->
        <a-card title="汇总统计" size="small" style="margin-bottom: 16px">
          <a-descriptions :column="3" size="small">
            <a-descriptions-item label="通过率">
              <span :style="{ color: getPassRateColor(drawerData.pass_rate), fontWeight: 600 }">
                {{ formatPercent(drawerData.pass_rate) }}（{{ drawerData.pass_count }}/{{ drawerData.total }}）
              </span>
            </a-descriptions-item>
            <a-descriptions-item label="平均响应时间">
              {{ drawerData.avg_duration_ms != null ? Math.round(drawerData.avg_duration_ms) + ' ms' : '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="平均Token">
              {{ drawerData.avg_tokens != null ? Math.round(drawerData.avg_tokens) : '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="格式合规率">
              {{ drawerData.format_valid_rate != null ? formatPercent(drawerData.format_valid_rate) : '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="首次通过轮次">
              {{ drawerData.first_pass_run != null ? '第 ' + drawerData.first_pass_run + ' 轮' : '从未通过' }}
            </a-descriptions-item>
            <a-descriptions-item label="连续失败">
              <a-tag :color="drawerData.has_consecutive_failures ? 'error' : 'success'">
                {{ drawerData.has_consecutive_failures ? '存在' : '无' }}
              </a-tag>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 每轮详情 -->
        <a-table
          :columns="runColumns"
          :data-source="drawerData.runs"
          row-key="run_index"
          :pagination="false"
          size="small"
          bordered
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'passed'">
              <a-tag :color="record.passed ? 'success' : 'error'">
                {{ record.passed ? '✓ 通过' : '✗ 失败' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'tokens'">
              {{ (record.input_tokens || 0) + (record.output_tokens || 0) }}
            </template>
            <template v-else-if="column.key === 'output_format_valid'">
              <a-tag :color="record.output_format_valid ? 'success' : 'warning'">
                {{ record.output_format_valid ? '合规' : '不合规' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'reason'">
              <a-typography-paragraph
                :ellipsis="{ rows: 2, expandable: true }"
                style="margin-bottom: 0"
              >
                {{ record.reason || '-' }}
              </a-typography-paragraph>
            </template>
          </template>
        </a-table>
      </div>
      </a-spin>
    </a-drawer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { UploadOutlined, DownloadOutlined, RobotOutlined } from '@ant-design/icons-vue';
import * as XLSX from 'xlsx';
import {
  getComparisonPlanDetail,
  updateComparisonPlan,
  addComparisonModel,
  removeComparisonModel,
  addComparisonCase,
  updateComparisonCase,
  removeComparisonCase,
  executeComparisonPlan,
  cancelComparisonPlan,
  getComparisonResults,
  getComparisonProgress,
  getComparisonRunLogs,
  getComparisonRunLogDetail,
  getComparisonResultDetail,
  importCsvCases,
  generateComparisonSummary
} from '@/api/modelComparison';
import { getModels } from '@/api/model';
import { getTestCases } from '@/api/evalTestCaseV2';

const router = useRouter();
const route = useRoute();

const planId = computed(() => route.params.id);

// 计划数据
const plan = ref({
  id: null,
  name: '',
  description: '',
  judge_model_id: null,
  runs_per_model: 1,
  status: 'draft',
  models: [],
  cases: []
});

const loading = ref(false);
const executing = ref(false);
const cancelling = ref(false);
const modelsLoading = ref(false);

// 所有可用模型列表
const availableModels = ref([]);

// 裁判模型 id（双向绑定）
const judgeModelId = ref(null);

// 添加模型弹窗
const addModelModalVisible = ref(false);
const selectedModelIds = ref([]);
const addingModel = ref(false);

// 用例内联编辑
const editingCaseId = ref(null);
const editingCaseValue = ref('');
const caseInputRef = ref(null);

// 添加用例弹窗
const addCaseModalVisible = ref(false);
const addingCase = ref(false);
const caseSearchLoading = ref(false);
const caseSearchResults = ref([]);
const addCaseForm = reactive({
  test_case_id: null,
  expected_description: ''
});

// 结果矩阵
const resultsLoading = ref(false);
const resultsRawData = ref(null);
const matrixData = reactive({
  columns: [],
  rows: []
});

// 统计摘要
const summaryStats = reactive({
  bestModel: null,
  cheapestModel: null,
  fastestModel: null,
  hardestCase: null
});

const aiSummary = reactive({ loading: false, text: '' });

// 执行明细抽屉
const drawerVisible = ref(false);
const drawerData = ref(null);
const drawerLoading = ref(false);

// 轮询定时器
let pollingTimer = null;
let logsPollingTimer = null;

// 执行进度
const progressInfo = reactive({ completed_runs: 0, total_runs: 0 });

// 当前选中的执行批次（null = 最新一次）
const selectedExecutionNo = ref(null);

// 执行日志面板
const runLogs = ref([]);
const logsLoading = ref(false);
const runLogsTotal = ref(0);
const logPagination = reactive({ current: 1, pageSize: 20 });
// 展开的日志条目 key 集合（input_prompt / judge_prompt 折叠控制）
const expandedPrompts = reactive({}); // key: `${log.id}_input` | `${log.id}_judge`

let planDetailRequest = null;
let resultsRequestId = 0;

// 用例表格列定义
const caseColumns = [
  { title: '用例名', dataIndex: 'case_name', key: 'case_name', width: 200 },
  { title: '期望描述', dataIndex: 'expected_description', key: 'expected_description' },
  { title: '操作', key: 'action', width: 80, fixed: 'right' }
];

// 执行明细表格列定义
const runColumns = [
  { title: '轮次', dataIndex: 'run_index', key: 'run_index', width: 60 },
  { title: '结果', dataIndex: 'passed', key: 'passed', width: 90 },
  { title: '响应时间(ms)', dataIndex: 'duration_ms', key: 'duration_ms', width: 120 },
  { title: 'Token总数', key: 'tokens', width: 100 },
  { title: '格式', dataIndex: 'output_format_valid', key: 'output_format_valid', width: 80 },
  { title: '判断理由', dataIndex: 'reason', key: 'reason' }
];

// 加载计划详情
const fetchPlanDetail = async () => {
  if (planDetailRequest) return planDetailRequest;
  planDetailRequest = (async () => {
    loading.value = true;
    try {
      const res = await getComparisonPlanDetail(planId.value);
      if (res.code === 200) {
        plan.value = res.data;
        judgeModelId.value = res.data.judge_model_id;
        if (res.data.execution_count > 0) {
          selectedExecutionNo.value = res.data.execution_count;
        }
        if (['completed', 'cancelled', 'failed'].includes(res.data.status) && res.data.execution_count > 0) {
          fetchResults();
          fetchRunLogs();
        }
      } else {
        message.error(res.message || '获取计划详情失败');
      }
    } catch (error) {
      message.error('获取计划详情失败：' + error.message);
    } finally {
      loading.value = false;
    }
  })();
  try {
    return await planDetailRequest;
  } finally {
    planDetailRequest = null;
  }
};

// 加载结果矩阵
const fetchResults = async () => {
  const requestId = ++resultsRequestId;
  resultsLoading.value = true;
  try {
    const res = await getComparisonResults(planId.value, selectedExecutionNo.value);
    if (res.code === 200 && requestId === resultsRequestId) {
      buildMatrixData(res.data);
    }
  } catch (error) {
    message.error('获取结果矩阵失败：' + error.message);
  } finally {
    if (requestId === resultsRequestId) resultsLoading.value = false;
  }
};

// 构建矩阵表格数据
const buildMatrixData = (data) => {
  resultsRawData.value = data;
  const { cases, models, matrix } = data;

  // 构建列定义：第一列为模型名，后续每个用例一列
  const cols = [
    { title: '模型', dataIndex: 'model_name', key: 'model_name', width: 200, fixed: 'left' }
  ];

  for (const c of cases) {
    cols.push({
      title: c.case_name,
      key: `case_${c.test_case_id}`,
      isCaseColumn: true,
      width: 140
    });
  }

  // 构建行数据：每行一个模型
  const rows = models.map((model) => {
    // 计算该模型整体平均通过率
    let totalPassCount = 0;
    let totalCount = 0;
    for (const c of cases) {
      const key = `${c.test_case_id}_${model.model_id}`;
      const cell = matrix[key];
      if (cell) {
        totalPassCount += cell.pass_count || 0;
        totalCount += cell.total || 0;
      }
    }
    const modelAvgRate = totalCount > 0 ? totalPassCount / totalCount : null;

    const row = {
      model_id: model.model_id,
      model_name: model.model_name,
      model_avg_rate: modelAvgRate
    };
    for (const c of cases) {
      const key = `${c.test_case_id}_${model.model_id}`;
      row[`case_${c.test_case_id}`] = matrix[key] || null;
    }
    return row;
  });

  matrixData.columns = cols;
  matrixData.rows = rows;

  // 重置摘要统计
  summaryStats.bestModel = null;
  summaryStats.cheapestModel = null;
  summaryStats.fastestModel = null;
  summaryStats.hardestCase = null;

  if (rows.length === 0) return;

  // 最高通过率模型
  let bestRow = null;
  for (const row of rows) {
    if (row.model_avg_rate == null) continue;
    if (bestRow == null || row.model_avg_rate > bestRow.model_avg_rate) {
      bestRow = row;
    }
  }
  if (bestRow) {
    summaryStats.bestModel = { model_name: bestRow.model_name, pass_rate: bestRow.model_avg_rate };
  }

  // 最低 Token 消耗模型
  let cheapestRow = null;
  let cheapestAvg = Infinity;
  for (const row of rows) {
    const values = [];
    for (const c of cases) {
      const cell = row[`case_${c.test_case_id}`];
      if (cell && cell.avg_tokens != null) values.push(Number(cell.avg_tokens));
    }
    if (values.length === 0) continue;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    if (avg < cheapestAvg) {
      cheapestAvg = avg;
      cheapestRow = row;
    }
  }
  if (cheapestRow) {
    summaryStats.cheapestModel = { model_name: cheapestRow.model_name, avg_tokens: cheapestAvg };
  }

  // 最快平均响应模型
  let fastestRow = null;
  let fastestAvg = Infinity;
  for (const row of rows) {
    const values = [];
    for (const c of cases) {
      const cell = row[`case_${c.test_case_id}`];
      if (cell && cell.avg_duration_ms != null) values.push(Number(cell.avg_duration_ms));
    }
    if (values.length === 0) continue;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    if (avg < fastestAvg) {
      fastestAvg = avg;
      fastestRow = row;
    }
  }
  if (fastestRow) {
    summaryStats.fastestModel = { model_name: fastestRow.model_name, avg_duration_ms: Math.round(fastestAvg) };
  }

  // 最难通过用例（所有模型对该 case 通过率均值最低）
  let hardestCase = null;
  let lowestCaseAvg = Infinity;
  for (const c of cases) {
    const values = [];
    for (const row of rows) {
      const cell = row[`case_${c.test_case_id}`];
      if (cell && cell.pass_rate != null) values.push(Number(cell.pass_rate));
    }
    if (values.length === 0) continue;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    if (avg < lowestCaseAvg) {
      lowestCaseAvg = avg;
      hardestCase = c;
    }
  }
  if (hardestCase) {
    summaryStats.hardestCase = { case_name: hardestCase.case_name, pass_rate: lowestCaseAvg };
  }
};

const handleGenerateSummary = async () => {
  aiSummary.loading = true;
  aiSummary.text = '';
  try {
    const res = await generateComparisonSummary(planId.value, selectedExecutionNo.value);
    if (res.code === 200) {
      aiSummary.text = res.data.summary || '';
    } else {
      message.error(res.message || '生成总结失败');
    }
  } catch (error) {
    message.error('生成总结失败：' + error.message);
  } finally {
    aiSummary.loading = false;
  }
};

const handleExportExcel = () => {
  if (!resultsRawData.value) return;
  const { cases, models, matrix } = resultsRawData.value;
  const wb = XLSX.utils.book_new();

  const summaryHeader = ['模型名称', '整体通过率', '平均响应(ms)', '平均Token数',
    ...cases.map(c => `[${c.case_name}] 通过率`)];
  const summaryRows = models.map(model => {
    let totalPass = 0, totalCount = 0, totalDuration = 0, totalTokens = 0, cellCount = 0;
    for (const c of cases) {
      const cell = matrix[`${c.test_case_id}_${model.model_id}`];
      if (cell) {
        totalPass += cell.pass_count || 0;
        totalCount += cell.total || 0;
        totalDuration += Number(cell.avg_duration_ms) || 0;
        totalTokens += Number(cell.avg_tokens) || 0;
        cellCount++;
      }
    }
    const overallRate = totalCount > 0 ? `${(totalPass / totalCount * 100).toFixed(1)}%` : '-';
    const avgDuration = cellCount > 0 ? Math.round(totalDuration / cellCount) : '-';
    const avgTokens = cellCount > 0 ? Math.round(totalTokens / cellCount) : '-';
    const caseRates = cases.map(c => {
      const cell = matrix[`${c.test_case_id}_${model.model_id}`];
      return cell ? `${(Number(cell.pass_rate) * 100).toFixed(1)}%` : '-';
    });
    return [model.model_name, overallRate, avgDuration, avgTokens, ...caseRates];
  });
  const ws1 = XLSX.utils.aoa_to_sheet([summaryHeader, ...summaryRows]);
  XLSX.utils.book_append_sheet(wb, ws1, '汇总');

  const planName = plan.value?.name || '对比计划';
  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `模型对比_${planName}_${date}.xlsx`);
};

// 加载 openrouter 模型列表
const fetchModels = async () => {
  if (availableModels.value.length > 0) return;
  modelsLoading.value = true;
  try {
    const res = await getModels({ pageSize: 500 });
    if (res.code === 200) {
      availableModels.value = res.data?.list || res.data || [];
    }
  } catch (error) {
    message.error('获取模型列表失败：' + error.message);
  } finally {
    modelsLoading.value = false;
  }
};

// 裁判模型变更
const handleJudgeModelChange = async (val) => {
  try {
    const res = await updateComparisonPlan(planId.value, { judge_model_id: val });
    if (res.code === 200) {
      message.success('裁判模型已更新');
    } else {
      message.error(res.message || '更新失败');
      judgeModelId.value = plan.value.judge_model_id;
    }
  } catch (error) {
    message.error('更新裁判模型失败：' + error.message);
    judgeModelId.value = plan.value.judge_model_id;
  }
};

// 打开添加模型弹窗
const openAddModelModal = async () => {
  selectedModelIds.value = [];
  addModelModalVisible.value = true;
  await fetchModels();
};

// 添加被测模型
const handleAddModel = async () => {
  if (!selectedModelIds.value.length) {
    message.warning('请选择要添加的模型');
    return;
  }
  addingModel.value = true;
  try {
    // 已关联模型的 id 集合，防止重复添加
    const existingIds = new Set((plan.value.models || []).map(m => m.model_id));
    const toAdd = selectedModelIds.value.filter(id => !existingIds.has(id));
    if (!toAdd.length) {
      message.warning('所选模型已全部添加过');
      return;
    }
    await Promise.all(toAdd.map(id => addComparisonModel(planId.value, { model_id: id })));
    message.success(`成功添加 ${toAdd.length} 个模型`);
    addModelModalVisible.value = false;
    selectedModelIds.value = [];
    fetchPlanDetail();
  } catch (error) {
    message.error('添加模型失败：' + error.message);
  } finally {
    addingModel.value = false;
  }
};

// 移除被测模型
const handleRemoveModel = async (model) => {
  try {
    const res = await removeComparisonModel(planId.value, model.id);
    if (res.code === 200) {
      message.success('模型已移除，点击"开始执行"以使用当前模型列表重新对比');
      fetchPlanDetail();
    } else {
      message.error(res.message || '移除失败');
    }
  } catch (error) {
    message.error('移除模型失败：' + error.message);
  }
};

// 模型选项过滤
const filterModelOption = (input, option) => {
  return option.children?.toLowerCase().includes(input.toLowerCase());
};

// 开始内联编辑期望描述
const startEditCase = (record) => {
  editingCaseId.value = record.id;
  editingCaseValue.value = record.expected_description || '';
  nextTick(() => {
    if (caseInputRef.value) {
      caseInputRef.value.focus?.();
    }
  });
};

// 取消内联编辑
const cancelEditCase = () => {
  editingCaseId.value = null;
  editingCaseValue.value = '';
};

// 保存内联编辑
const saveEditCase = async (record) => {
  const newVal = editingCaseValue.value.trim();
  editingCaseId.value = null;
  if (newVal === (record.expected_description || '').trim()) return;
  try {
    const res = await updateComparisonCase(planId.value, record.id, { expected_description: newVal });
    if (res.code === 200) {
      record.expected_description = newVal;
      message.success('期望描述已更新');
    } else {
      message.error(res.message || '更新失败');
    }
  } catch (error) {
    message.error('更新期望描述失败：' + error.message);
  }
};

// 移除用例
const handleRemoveCase = async (record) => {
  try {
    const res = await removeComparisonCase(planId.value, record.id);
    if (res.code === 200) {
      message.success('用例已移除');
      fetchPlanDetail();
    } else {
      message.error(res.message || '移除失败');
    }
  } catch (error) {
    message.error('移除用例失败：' + error.message);
  }
};

// 打开添加用例弹窗
const openAddCaseModal = async () => {
  resetAddCaseForm();
  addCaseModalVisible.value = true;
  // 打开弹窗时预加载最近20条用例
  caseSearchLoading.value = true;
  try {
    const res = await getTestCases({ pageSize: 20 });
    if (res.code === 200) {
      caseSearchResults.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('加载测试用例失败:', error);
  } finally {
    caseSearchLoading.value = false;
  }
};

// 重置添加用例表单
const resetAddCaseForm = () => {
  Object.assign(addCaseForm, {
    test_case_id: null,
    expected_description: ''
  });
  caseSearchResults.value = [];
  addCaseModalVisible.value = false;
};

// 远程搜索用例
let caseSearchTimer = null;
const handleCaseSearch = (keyword) => {
  if (caseSearchTimer) clearTimeout(caseSearchTimer);
  if (!keyword) {
    caseSearchResults.value = [];
    return;
  }
  caseSearchTimer = setTimeout(async () => {
    caseSearchLoading.value = true;
    try {
      const res = await getTestCases({ keyword, pageSize: 20 });
      if (res.code === 200) {
        caseSearchResults.value = res.data?.list || [];
      }
    } catch (error) {
      console.error('搜索测试用例失败:', error);
    } finally {
      caseSearchLoading.value = false;
    }
  }, 300);
};

// 选中用例后处理
const handleCaseSelectChange = (val) => {
  addCaseForm.test_case_id = val;
};

// 提交添加用例
const handleAddCase = async () => {
  if (!addCaseForm.test_case_id) {
    message.warning('请选择测试用例');
    return;
  }
  if (!addCaseForm.expected_description.trim()) {
    message.warning('期望描述不能为空');
    return;
  }
  addingCase.value = true;
  try {
    const res = await addComparisonCase(planId.value, {
      test_case_id: addCaseForm.test_case_id,
      expected_description: addCaseForm.expected_description.trim()
    });
    if (res.code === 200) {
      message.success('用例添加成功');
      resetAddCaseForm();
      fetchPlanDetail();
    } else {
      message.error(res.message || '添加失败');
    }
  } catch (error) {
    message.error('添加用例失败：' + error.message);
  } finally {
    addingCase.value = false;
  }
};

// 执行计划
const handleExecute = async () => {
  executing.value = true;
  try {
    const res = await executeComparisonPlan(planId.value);
    if (res.code === 200) {
      message.success('执行已启动，将自动刷新状态');
      plan.value.status = 'running';
      // 新执行批次的 execution_no 尚未确定，先清空旧数据避免误导
      selectedExecutionNo.value = null;
      Object.assign(progressInfo, { completed_runs: 0, total_runs: 0 });
      startPolling();
    } else {
      message.error(res.message || '启动执行失败');
    }
  } catch (error) {
    message.error('执行失败：' + error.message);
  } finally {
    executing.value = false;
  }
};

// 取消执行
const handleCancel = async () => {
  cancelling.value = true;
  try {
    const res = await cancelComparisonPlan(planId.value);
    if (res.code === 200) {
      message.warning('已发送停止信号，正在排队的任务将陆续中止');
      plan.value.status = 'cancelled';
      stopPolling();
    } else {
      message.error(res.message || '停止失败');
    }
  } catch (error) {
    message.error('停止失败：' + error.message);
  } finally {
    cancelling.value = false;
  }
};

// 拉取执行日志（追加新增条目）
const fetchRunLogs = async () => {
  logsLoading.value = true;
  try {
    const res = await getComparisonRunLogs(planId.value, selectedExecutionNo.value, logPagination);
    if (res.code === 200) {
      runLogs.value = res.data?.items || [];
      runLogsTotal.value = res.data?.total || 0;
    }
  } catch (error) {
    console.error('拉取执行日志失败:', error);
  } finally {
    logsLoading.value = false;
  }
};

const toggleLogSection = async (log, section) => {
  const key = `${log.id}_${section}`;
  if (expandedPrompts[key]) {
    expandedPrompts[key] = false;
    return;
  }
  if (!log.detailLoaded) {
    try {
      const res = await getComparisonRunLogDetail(planId.value, log.id);
      if (res.code === 200) Object.assign(log, res.data, { detailLoaded: true });
    } catch (error) {
      message.error('加载日志详情失败：' + error.message);
      return;
    }
  }
  expandedPrompts[key] = true;
};

// 启动轮询：3秒查进度，8秒拉日志
const startPolling = () => {
  stopPolling();
  // 执行开始，清空旧日志，selectedExecutionNo 会在进度完成后同步为新批次
  runLogs.value = [];
  runLogsTotal.value = 0;
  // 立即拉一次进度（不拉旧批次日志，等有新数据后才拉）

  pollingTimer = setInterval(async () => {
    try {
      const res = await getComparisonProgress(planId.value);
      if (res.code === 200) {
        const { status, completed_runs, total_runs, error_message, execution_count } = res.data;
        progressInfo.completed_runs = completed_runs || 0;
        progressInfo.total_runs = total_runs || 0;
        plan.value.status = status;
        // 同步当前执行批次，确保日志轮询拉取正确批次
        if (execution_count && execution_count > 0) {
          selectedExecutionNo.value = execution_count;
        }

        if (status === 'completed' || status === 'failed') {
          stopPolling();
          if (status === 'completed') {
            message.success('执行完成，正在加载结果矩阵');
            // fetchPlanDetail 内部会同步 selectedExecutionNo、fetchResults、fetchRunLogs
            await fetchPlanDetail();
          } else {
            message.error('执行失败' + (error_message ? `：${error_message}` : ''));
          }
        }
      }
    } catch (error) {
      console.error('轮询进度失败:', error);
    }
  }, 3000);

  logsPollingTimer = setInterval(fetchRunLogs, 8000);
};

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
  }
  if (logsPollingTimer) {
    clearInterval(logsPollingTimer);
    logsPollingTimer = null;
  }
};

// 打开执行明细抽屉
const openRunDrawer = async (cellData) => {
  drawerData.value = { ...cellData, runs: [] };
  drawerVisible.value = true;
  drawerLoading.value = true;
  try {
    const res = await getComparisonResultDetail(planId.value, {
      executionNo: selectedExecutionNo.value,
      testCaseId: cellData.test_case_id,
      modelId: cellData.model_id
    });
    if (res.code === 200 && drawerVisible.value) {
      drawerData.value = { ...cellData, runs: res.data.runs || [] };
    }
  } catch (error) {
    message.error('加载执行明细失败：' + error.message);
  } finally {
    drawerLoading.value = false;
  }
};

// 格式化通过率百分比
const formatPercent = (rate) => {
  if (rate == null) return '-';
  return (rate * 100).toFixed(1) + '%';
};

// 通过率颜色
const getPassRateColor = (rate) => {
  if (rate == null) return '#000';
  if (rate >= 0.8) return '#52c41a';
  if (rate >= 0.5) return '#fa8c16';
  return '#ff4d4f';
};

// 状态颜色
const getStatusColor = (status) => {
  const colors = {
    draft: 'default',
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning'
  };
  return colors[status] || 'default';
};

// 状态文本
const getStatusText = (status) => {
  const texts = {
    draft: '草稿',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已停止'
  };
  return texts[status] || status;
};

// 切换执行批次
const handleExecutionNoChange = async (val) => {
  selectedExecutionNo.value = val;
  logPagination.current = 1;
  await fetchRunLogs();
  fetchResults();
};

const goBack = () => {
  router.push('/eval-v2/model-comparison');
};

// CSV 导入弹窗相关状态
const showCsvImportModal = ref(false);
const csvPreviewRows = ref([]);
const importResult = ref(null);
const csvImporting = ref(false);
const csvFileInputRef = ref(null);

// 打开 CSV 导入弹窗
const openCsvImportModal = () => {
  showCsvImportModal.value = true;
};

// 重置预览（重新选择文件）
const resetCsvPreview = () => {
  csvPreviewRows.value = [];
  if (csvFileInputRef.value) csvFileInputRef.value.value = '';
};

// 关闭 CSV 导入弹窗（有导入记录则刷新页面）
const closeCsvImportModal = () => {
  if (importResult.value) {
    fetchPlanDetail();
  }
  showCsvImportModal.value = false;
  csvPreviewRows.value = [];
  importResult.value = null;
  if (csvFileInputRef.value) csvFileInputRef.value.value = '';
};

// 处理 CSV 文件选择
const handleCsvFile = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    const workbook = XLSX.read(reader.result, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows2d = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    if (rows2d.length < 2) {
      message.error('CSV 文件为空或只有表头');
      return;
    }

    const headers = rows2d[0].map(h => String(h || '').trim().toLowerCase());

    // 列索引映射（大小写不敏感）
    const taskIdIdx = headers.findIndex(h => h === 'task_id' || h === '任务id');
    const stepOrderIdx = headers.findIndex(h => h === '步骤顺序' || h === 'step_order' || h === '步骤');
    const expectedIdx = headers.findIndex(h => h === '判断标准' || h === 'expected_description' || h === '期望描述');

    if (taskIdIdx === -1 || stepOrderIdx === -1 || expectedIdx === -1) {
      message.error('CSV 格式错误，请检查列名是否包含：task_id、步骤顺序、判断标准');
      return;
    }

    const parsedRows = rows2d.slice(1)
      .map(row => {
        const stepRaw = String(row[stepOrderIdx] ?? '').trim();
        const stepParts = stepRaw.includes('.') ? stepRaw.split('.') : [stepRaw, null];
        return {
          task_id: String(row[taskIdIdx] ?? '').trim(),
          step_order: parseInt(stepParts[0], 10),
          sub_step_order: stepParts[1] !== null ? parseInt(stepParts[1], 10) : null,
          expected_description: String(row[expectedIdx] ?? '').trim()
        };
      })
      .filter(row => row.task_id !== '');

    if (parsedRows.length === 0) {
      message.error('CSV 中没有有效数据行');
      return;
    }

    csvPreviewRows.value = parsedRows;
  };
  reader.onerror = () => {
    message.error('文件读取失败，请重试');
  };
  reader.readAsArrayBuffer(file);
};

// 执行导入
const handleImportCsv = async () => {
  csvImporting.value = true;
  try {
    const res = await importCsvCases(planId.value, csvPreviewRows.value);
    if (res.code === 200) {
      importResult.value = res.data;
    } else {
      message.error('导入失败：' + res.message);
    }
  } catch (error) {
    message.error('导入失败：' + error.message);
  } finally {
    csvImporting.value = false;
  }
};

onMounted(async () => {
  await fetchPlanDetail();
  // 若正在执行中，则启动轮询（fetchPlanDetail 已设好 selectedExecutionNo）
  if (plan.value.status === 'running') {
    startPolling();
  }
  // 提前加载模型列表（异步，不阻塞页面）
  fetchModels();
});

onUnmounted(() => {
  stopPolling();
  if (caseSearchTimer) clearTimeout(caseSearchTimer);
});
</script>

<style scoped>
.model-comparison-detail {
  padding: 24px;
}
</style>
